import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Badge,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Menu,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    CheckCircle as LicenseGrantedIcon,
    EventBusy as AttendanceIcon,
    MenuBook as CourseContentIcon,
    Notifications as NotificationsIcon,
    NotificationsNone as NotificationsNoneIcon,
    WarningAmber as LicenseExpiringIcon,
} from '@mui/icons-material';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationResponseDto } from '../../helper-types/dtos/notification/responses/notification-response-dto';
import { NotificationType } from '../../helper-types/general/enums/notification-type';
import { formatDateTime } from '../../shared/locale-format';

const NOTIFICATION_TYPE_ICONS: Record<NotificationType, React.ReactElement> = {
    [NotificationType.LicenseGranted]: <LicenseGrantedIcon fontSize="small" color="success" />,
    [NotificationType.LicenseExpiringSoon]: <LicenseExpiringIcon fontSize="small" color="warning" />,
    [NotificationType.CourseContentPublished]: <CourseContentIcon fontSize="small" color="info" />,
    [NotificationType.AttendanceMarkedAbsent]: <AttendanceIcon fontSize="small" color="error" />,
};

interface NotificationBellProps {
    size?: 'small' | 'medium';
}

export default function NotificationBell({ size = 'medium' }: NotificationBellProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { unreadCount, notifications, loading, fetchNotifications, markAsRead, markAllAsRead } = useNotifications(true);
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        void fetchNotifications();
    };

    const handleClose = () => setAnchorEl(null);

    const handleNotificationClick = async (notification: NotificationResponseDto) => {
        if (!notification.isRead) {
            await markAsRead(notification.id);
        }
        handleClose();
        if (notification.linkPath) {
            navigate(notification.linkPath);
        }
    };

    return (
        <>
            <IconButton
                onClick={handleOpen}
                aria-label={t('notifications.title')}
                size={size}
            >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                    {unreadCount > 0 ? <NotificationsIcon fontSize={size} /> : <NotificationsNoneIcon fontSize={size} />}
                </Badge>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: { width: 360, maxWidth: '90vw', maxHeight: 480 },
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {t('notifications.title')}
                    </Typography>
                    {unreadCount > 0 && (
                        <Button size="small" onClick={() => void markAllAsRead()}>
                            {t('notifications.markAllRead')}
                        </Button>
                    )}
                </Box>
                <Divider />

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {!loading && notifications.length === 0 && (
                    <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('notifications.empty')}
                        </Typography>
                    </Box>
                )}

                {!loading && notifications.map((notification) => (
                    <Box
                        key={notification.id}
                        onClick={() => void handleNotificationClick(notification)}
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            px: 2,
                            py: 1.25,
                            cursor: 'pointer',
                            backgroundColor: notification.isRead ? 'transparent' : (theme) => alpha(theme.palette.primary.main, 0.06),
                            '&:hover': {
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                            },
                        }}
                    >
                        <Box sx={{ pt: 0.25 }}>{NOTIFICATION_TYPE_ICONS[notification.type]}</Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" fontWeight={notification.isRead ? 400 : 700} noWrap>
                                {notification.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                {notification.message}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                                {formatDateTime(notification.createdAt)}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Menu>
        </>
    );
}
